import { createReducer } from '@reduxjs/toolkit'
import {
  addStoreys,
  addRoof,
  setSelectedBuildingMode,
  setSelectedStoreyId,
  setSelectedConstructionTab,
  setShowUploadModal,
  setShowProgressModal,
  setIs3DViewActive,
  resetConstructionState,
  setIs3DFetchBuildingInfo,
  setSelectedConstructionPage,
  setSelectedElementsIds,
  updateHiddenStoreyElements,
  setConvertData,
  updateBuildingElementStatus,
  setFileName,
  resetBuildingElementsStatus,
  setSelectedTool,
  removeWall,
  removeWindow,
  removeDoor,
  createWall,
  setSelectedToolProps,
  removeStructuralElement,
  createWindow,
  createDoor,
  createStructuralElement,
  editWall,
  setBuildingDocument,
  updateBuildingDocumentPage,
  updateBuildingDocumentSelectedPage,
  editStructuralElement,
  editWindow,
  editDoor,
  resetLastUpdatedElementId,
  moveRoof,
  updateGridSettings,
  toggleHideShowOption,
  setHideShowOption,
  updateConvertData,
  updateStatusMessage,
  setSelectedEditorTab,
  setIs2DViewActive,
  setBuildigBimObjects,
} from '../actions/construction'
import {
  BuildingBeamSize,
  BuildingDocument,
  BuildingElementStatus,
  BuildingElementSubType,
  BuildingElementType,
  BuildingOpeningSize,
  BuildingTool,
  BuildingViewMode,
  ConvertData,
  defaultGridSettings,
  defaultHideShowOptions,
  GridSettings,
  Roof,
  Room,
  HideShowOption,
  Storey,
} from '../models/Construction'
import { StoreyElementsVisibility } from '../utils/EventEmitter'
import {
  BuildingStructuralTypes,
  RectangleFramingElementType,
} from '../models/Structural'
import { WallAnchor } from '../visualization/tools/useEditWallTool'
import { BimObject } from '../models/Bim'

export enum ConstructionTab {
  Editor = 'Editor',
  Validation = 'Validation',
  TakeOff = 'Takeoff',
  Permit = 'Permit',
}

export enum ConstructionPage {
  Architecture = 'Architecture',
  Engineering = 'Engineering',
}

export enum EditorTab {
  Orbit = 'Orbit',
  Bim = 'BIM',
  Objects = 'Objects',
}

export enum EditorDocumentsTab {
  Rulebook = 'Rulebook',
  Filehub = 'File Hub',
  Calculations = 'Calculations',
}

interface ConstructionState {
  fileName: string
  convertData: ConvertData
  documents: BuildingDocument[]
  bimObjects: BimObject[]
  buildingElementStatus: {
    elementType: BuildingElementType
    elementSubtype?: BuildingElementSubType
    status: BuildingElementStatus
  }[]
  hiddenStoreyElements: StoreyElementsVisibility
  lastUpdatedElementId: string | null
  selectedStoreyId: string | null
  selectedElementsIds: string[]
  selectedBuildingMode: BuildingViewMode
  selectedConstructionTab: ConstructionTab
  selectedConstructionPage: ConstructionPage
  selectedEditorTab: EditorTab
  selectedTool: BuildingTool | null
  selectedToolProps: {
    wallThickness: number
    wallType: BuildingElementSubType
    windowSize: BuildingOpeningSize
    doorSize: BuildingOpeningSize
    beamSize: BuildingBeamSize
  }
  showUploadModal: boolean
  showProgressModal: boolean
  showSketchAnimation: boolean
  hideShowOptions: Record<HideShowOption, boolean>
  gridSettings: GridSettings
  is3DViewActive?: boolean
  is2DViewActive?: boolean
  isFetch3DBuildingInfo?: boolean
}

const initialBuildingElementStatus = [
  {
    elementType: BuildingElementType.Wall,
    elementSubtype: BuildingElementSubType.Exterior,
    status: BuildingElementStatus.NotStarted,
  },
  {
    elementType: BuildingElementType.Wall,
    elementSubtype: BuildingElementSubType.Interior,
    status: BuildingElementStatus.NotStarted,
  },
  {
    elementType: BuildingElementType.Door,
    status: BuildingElementStatus.NotStarted,
  },
  {
    elementType: BuildingElementType.Window,
    status: BuildingElementStatus.NotStarted,
  },
  {
    elementType: BuildingElementType.Room,
    status: BuildingElementStatus.NotStarted,
  },
  {
    elementType: BuildingElementType.Roof,
    status: BuildingElementStatus.NotStarted,
  },
  {
    elementType: BuildingElementType.Structural,
    status: BuildingElementStatus.NotStarted,
  },
]

const initialState: ConstructionState = {
  fileName: '',
  convertData: {
    building: {
      storeys: [],
      roofs: [],
      stairs: [],
      foundations: [],
      has_elements: {
        exterior_walls: false,
        interior_walls: false,
        doors: false,
        windows: false,
        rooms: false,
        roof: false,
      },
      has_structural: false,
      floor_area: 0,
      lot_area: 0,
    },
    message: '',
    context_id: '',
    json_s3_key: '',
  },
  documents: [],
  bimObjects: [],
  selectedTool: null,
  buildingElementStatus: initialBuildingElementStatus,
  hiddenStoreyElements: {},
  lastUpdatedElementId: null,
  selectedStoreyId: null,
  selectedElementsIds: [],
  selectedBuildingMode: BuildingViewMode.WholeBuilding,
  selectedConstructionTab: ConstructionTab.Editor,
  selectedConstructionPage: ConstructionPage.Architecture,
  selectedEditorTab: EditorTab.Objects,
  is3DViewActive: true,
  hideShowOptions: defaultHideShowOptions,
  showUploadModal: false,
  showProgressModal: false,
  showSketchAnimation: false,
  selectedToolProps: {
    wallThickness: 3.5,
    wallType: BuildingElementSubType.Interior,
    windowSize: BuildingOpeningSize.Medium,
    doorSize: BuildingOpeningSize.Medium,
    beamSize: BuildingBeamSize.Size1,
  },
  gridSettings: defaultGridSettings,
}

const constructionReducer = createReducer(initialState, (builder) => {
  builder
    .addCase(setConvertData, (state, action) => {
      const convertData = action.payload
      if (!convertData.building.has_elements) {
        convertData.building.has_elements = {
          exterior_walls: true,
          interior_walls: true,
          doors: true,
          windows: true,
          rooms: true,
          roof: true,
        }
      }

      if (!!state.convertData.building.storeys.length) {
        const buildingHadAllElements = Object.values(
          state.convertData.building.has_elements,
        ).every((value) => !!value)
        if (!buildingHadAllElements) {
          state.showSketchAnimation = true
        }
      } else {
        const buildingHasAllElements = Object.values(
          convertData.building.has_elements,
        ).every((value) => !!value)
        if (!buildingHasAllElements) {
          state.showSketchAnimation = true
        }
      }

      state.convertData = convertData
    })
    .addCase(setBuildigBimObjects, (state, action) => {
      state.bimObjects = action.payload
    })
    .addCase(setFileName, (state, action) => {
      state.fileName = action.payload
    })
    .addCase(setBuildingDocument, (state, action) => {
      const documentIndex = state.documents.findIndex(
        (document) => document.type === action.payload.type,
      )
      if (documentIndex !== -1) {
        state.documents[documentIndex] = action.payload
      } else {
        state.documents.push(action.payload)
      }
    })
    .addCase(updateBuildingDocumentPage, (state, action) => {
      if (!state.documents) {
        return
      }

      const documentIndex = state.documents.findIndex(
        (document) => document.file.id === action.payload.fileId,
      )
      if (documentIndex === -1) {
        return
      }

      const pageIndex = state.documents[documentIndex].file.pages.findIndex(
        (page) => page.id === action.payload.page.id,
      )
      if (pageIndex !== -1) {
        state.documents[documentIndex].file.pages[pageIndex] = {
          ...state.documents[documentIndex].file.pages[pageIndex],
          ...action.payload.page,
        }
      }
    })
    .addCase(updateBuildingDocumentSelectedPage, (state, action) => {
      if (!state.documents) {
        return
      }

      const documentIndex = state.documents.findIndex(
        (document) => document.file.id === action.payload.fileId,
      )
      if (documentIndex === -1) {
        return
      }

      state.documents[documentIndex].selectedPageId = action.payload.pageId
    })
    .addCase(updateConvertData, (state, action) => {
      state.convertData = action.payload.convertData
      state.lastUpdatedElementId = action.payload.elementId
    })
    .addCase(updateStatusMessage, (state, action) => {
      state.convertData.message = action.payload
    })
    .addCase(resetLastUpdatedElementId, (state, action) => {
      state.lastUpdatedElementId = null
    })
    .addCase(addStoreys, (state, action) => {
      const storeys = [
        ...state.convertData.building.storeys,
        ...action.payload.storeys,
      ]

      const storeyIds = new Set<string>()

      state.convertData.building.storeys = storeys.reduce<Storey[]>(
        (acc, current) => {
          if (!storeyIds.has(current.id)) {
            acc.push(current)
            storeyIds.add(current.id)
          }

          return acc
        },
        [],
      )
    })
    .addCase(createWall, (state, action) => {
      const { storeyId, ...wallProps } = action.payload
      const storeys = state.convertData.building.storeys
      const storey = storeyId
        ? (storeys.find((storey) => storey.id === storeyId) ?? storeys[0])
        : storeys[0]
      storey.walls.push({
        ...wallProps,
        slope: 0,
        rooms: [],
        windows: [],
        doors: [],
        studs: [],
      })
    })
    .addCase(editWall, (state, action) => {
      const { id: wallId, center_point, ...other } = action.payload
      const storeys = state.convertData.building.storeys
      for (const storey of storeys) {
        const wall = storey.walls.find((wall) => wall.id === wallId)

        if (wall) {
          wall.center_point = center_point

          if ('length' in other) {
            const { length, anchor } = other
            const oldLength = wall.length
            wall.length = length
            if (anchor === WallAnchor.End || anchor === WallAnchor.Center) {
              let diff = 0
              if (anchor === WallAnchor.End) {
                diff = length - oldLength
              } else {
                diff = (length - oldLength) / 2
              }

              wall.doors.forEach(
                (door) => (door.center_location_on_wall += diff),
              )
              wall.windows.forEach(
                (window) => (window.center_location_on_wall += diff),
              )
            }
          }

          if ('thickness' in other) {
            const { thickness } = other
            wall.thickness = thickness
            wall.doors.forEach((door) => (door.thickness = thickness))
            wall.windows.forEach((window) => (window.thickness = thickness))
          }

          return
        }
      }
    })
    .addCase(removeWall, (state, action) => {
      const wallId = action.payload.wallId
      const storeys = state.convertData.building.storeys
      for (const storey of storeys) {
        const wall = storey.walls.find((wall) => wall.id === wallId)

        if (wall) {
          storey.walls = storey.walls.filter((wall) => wall.id !== wallId)
          storey.rooms = storey.rooms.map<Room>((room) => {
            return {
              ...room,
              walls: room.walls.filter((id) => id !== wallId),
            }
          })
          storey.selected_shear_walls =
            storey.selected_shear_walls?.filter(
              (shearWall) => shearWall.wall_id !== wallId,
            ) ?? []
          storey.load_bearing_walls =
            storey.load_bearing_walls?.filter(
              (loadBearingWall) => loadBearingWall.wall_id !== wallId,
            ) ?? []

          return
        }
      }
    })
    .addCase(createWindow, (state, action) => {
      const walls = state.convertData.building.storeys.flatMap(
        (storey) => storey.walls,
      )
      const wall = walls.find((wall) => wall.id === action.payload.wall_id)
      if (wall) {
        wall.windows.push({
          ...action.payload,
          headers: [],
          studs: [],
        })
      }
    })
    .addCase(editWindow, (state, action) => {
      const {
        id,
        width,
        height,
        wall_id: wallId,
        sill_height: sillHeight,
        center_location_on_wall: centerLocationOnWall,
      } = action.payload
      const walls = state.convertData.building.storeys.flatMap(
        (storey) => storey.walls,
      )
      const wall = walls.find((wall) => wall.id === wallId)
      if (wall) {
        const window = wall.windows.find((window) => window.id === id)
        if (window) {
          window.width = width
          window.height = height
          window.sill_height = sillHeight
          window.center_location_on_wall = centerLocationOnWall
        }
      }
    })
    .addCase(removeWindow, (state, action) => {
      const windowId = action.payload.windowId
      const walls = state.convertData.building.storeys.flatMap(
        (storey) => storey.walls,
      )

      for (const wall of walls) {
        const window = wall.windows.find((window) => window.id === windowId)

        if (window) {
          wall.windows = wall.windows.filter((window) => window.id !== windowId)
          return
        }
      }
    })
    .addCase(createDoor, (state, action) => {
      const walls = state.convertData.building.storeys.flatMap(
        (storey) => storey.walls,
      )
      const wall = walls.find((wall) => wall.id === action.payload.wall_id)
      if (wall) {
        wall.doors.push({
          ...action.payload,
          hinge_side: 'left',
          headers: [],
          studs: [],
        })
      }
    })
    .addCase(editDoor, (state, action) => {
      const {
        id,
        width,
        height,
        wall_id: wallId,
        sill_height: sillHeight,
        center_location_on_wall: centerLocationOnWall,
      } = action.payload
      const walls = state.convertData.building.storeys.flatMap(
        (storey) => storey.walls,
      )
      const wall = walls.find((wall) => wall.id === wallId)
      if (wall) {
        const door = wall.doors.find((door) => door.id === id)
        if (door) {
          door.width = width
          door.height = height
          door.sill_height = sillHeight
          door.center_location_on_wall = centerLocationOnWall
        }
      }
    })
    .addCase(removeDoor, (state, action) => {
      const doorId = action.payload.doorId
      const walls = state.convertData.building.storeys.flatMap(
        (storey) => storey.walls,
      )

      for (const wall of walls) {
        const door = wall.doors.find((door) => door.id === doorId)

        if (door) {
          wall.doors = wall.doors.filter((door) => door.id !== doorId)
          return
        }
      }
    })
    .addCase(createStructuralElement, (state, action) => {
      switch (action.payload.elementType) {
        case RectangleFramingElementType.Beam:
          const storeyIndex = state.convertData.building.storeys.findIndex(
            (storey) => storey.id === action.payload.storeyId,
          )
          if (storeyIndex !== -1) {
            state.convertData.building.storeys[storeyIndex].beams.push(
              action.payload.element,
            )
          }
      }
    })
    .addCase(editStructuralElement, (state, action) => {
      switch (action.payload.elementType) {
        case RectangleFramingElementType.Beam:
          for (const storey of state.convertData.building.storeys) {
            const beamIndex = storey.beams.findIndex(
              (beam) => beam.id === action.payload.id,
            )
            if (beamIndex !== -1) {
              storey.beams[beamIndex].center_point = action.payload.center_point
              storey.beams[beamIndex].length = action.payload.length
              break
            }
          }
      }
    })
    .addCase(removeStructuralElement, (state, action) => {
      const walls = state.convertData.building.storeys.flatMap(
        (storey) => storey.walls,
      )
      switch (action.payload.structuralType) {
        case BuildingStructuralTypes.Beam:
          for (const storey of state.convertData.building.storeys) {
            const beamIndex = storey.beams.findIndex(
              (beam) => beam.id === action.payload.elementId,
            )
            if (beamIndex !== -1) {
              storey.beams.splice(beamIndex, 1)
              break
            }
          }
          break
        case BuildingStructuralTypes.Joist:
          for (const storey of state.convertData.building.storeys) {
            const spanIndex = storey.spans.findIndex((span) =>
              span.joists.some(
                (joist) => joist.id === action.payload.elementId,
              ),
            )
            if (spanIndex !== -1) {
              const joistIndex = storey.spans[spanIndex].joists.findIndex(
                (joist) => joist.id === action.payload.elementId,
              )
              if (joistIndex !== -1) {
                storey.spans[spanIndex].joists.splice(joistIndex, 1)
                break
              }
            }
          }
          break
        case BuildingStructuralTypes.Stud:
          for (const wall of walls) {
            let studIndex = wall.studs.findIndex(
              (stud) => stud.id === action.payload.elementId,
            )
            if (studIndex !== -1) {
              wall.studs.splice(studIndex, 1)
              break
            }

            const windowIndex = wall.windows.findIndex((window) =>
              window.studs.some((stud) => stud.id === action.payload.elementId),
            )
            if (windowIndex !== -1) {
              studIndex = wall.windows[windowIndex].studs.findIndex(
                (stud) => stud.id === action.payload.elementId,
              )
              if (studIndex !== -1) {
                wall.windows[windowIndex].studs.splice(studIndex, 1)
                break
              }
            }

            const doorIndex = wall.doors.findIndex((door) =>
              door.studs.some((stud) => stud.id === action.payload.elementId),
            )
            if (doorIndex !== -1) {
              studIndex = wall.doors[doorIndex].studs.findIndex(
                (stud) => stud.id === action.payload.elementId,
              )
              if (studIndex !== -1) {
                wall.doors[doorIndex].studs.splice(studIndex, 1)
                break
              }
            }
          }
          break
        case BuildingStructuralTypes.Header:
          for (const wall of walls) {
            let headerIndex = -1
            const windowIndex = wall.windows.findIndex((window) =>
              window.headers.some(
                (header) => header.id === action.payload.elementId,
              ),
            )
            if (windowIndex !== -1) {
              headerIndex = wall.windows[windowIndex].headers.findIndex(
                (header) => header.id === action.payload.elementId,
              )
              if (headerIndex !== -1) {
                wall.windows[windowIndex].headers.splice(headerIndex, 1)
                break
              }
            }

            const doorIndex = wall.doors.findIndex((door) =>
              door.headers.some(
                (header) => header.id === action.payload.elementId,
              ),
            )
            if (doorIndex !== -1) {
              headerIndex = wall.doors[doorIndex].headers.findIndex(
                (header) => header.id === action.payload.elementId,
              )
              if (headerIndex !== -1) {
                wall.doors[doorIndex].headers.splice(headerIndex, 1)
                break
              }
            }
          }
          break
      }
    })
    .addCase(addRoof, (state, action) => {
      const roofs = [...state.convertData.building.roofs, action.payload.roof]

      const roofIds = new Set<string>()

      state.convertData.building.roofs = roofs.reduce<Roof[]>(
        (acc, current) => {
          if (!roofIds.has(current.id)) {
            acc.push(current)
            roofIds.add(current.id)
          }

          return acc
        },
        [],
      )
    })
    .addCase(setSelectedStoreyId, (state, action) => {
      state.selectedStoreyId = action.payload
    })
    .addCase(setSelectedElementsIds, (state, action) => {
      state.selectedElementsIds = [...action.payload]
    })
    .addCase(setSelectedBuildingMode, (state, action) => {
      state.selectedBuildingMode = action.payload.mode
    })
    .addCase(setSelectedConstructionTab, (state, action) => {
      state.selectedConstructionTab = action.payload
      state.selectedTool = null
    })
    .addCase(setSelectedConstructionPage, (state, action) => {
      state.selectedConstructionPage = action.payload
      state.selectedTool = null
    })
    .addCase(setSelectedEditorTab, (state, action) => {
      state.selectedEditorTab = action.payload
      state.selectedTool = null
    })
    .addCase(setSelectedTool, (state, action) => {
      state.selectedTool =
        state.selectedTool === action.payload ? null : action.payload
    })
    .addCase(setSelectedToolProps, (state, action) => {
      state.selectedToolProps = {
        ...state.selectedToolProps,
        ...action.payload,
      }
    })
    .addCase(setShowUploadModal, (state, action) => {
      state.showUploadModal = action.payload
    })
    .addCase(setShowProgressModal, (state, action) => {
      state.showProgressModal = action.payload
    })
    .addCase(setIs3DViewActive, (state, action) => {
      state.is3DViewActive = action.payload
    })
    .addCase(setIs2DViewActive, (state, action) => {
      state.is2DViewActive = action.payload
    })
    .addCase(setIs3DFetchBuildingInfo, (state, action) => {
      state.isFetch3DBuildingInfo = action.payload
    })
    .addCase(updateHiddenStoreyElements, (state, action) => {
      state.hiddenStoreyElements = {
        ...state.hiddenStoreyElements,
        ...action.payload,
      }
    })
    .addCase(updateBuildingElementStatus, (state, action) => {
      const buildingElementIndex = state.buildingElementStatus.findIndex(
        (element) =>
          element.elementType === action.payload.elementType &&
          element.elementSubtype === action.payload.elementSubtype,
      )

      if (buildingElementIndex !== -1) {
        state.buildingElementStatus[buildingElementIndex].status =
          action.payload.status
      }
    })
    .addCase(updateGridSettings, (state, action) => {
      state.gridSettings = { ...state.gridSettings, ...action.payload }
    })
    .addCase(toggleHideShowOption, (state, action) => {
      const option = action.payload
      state.hideShowOptions[option] = !state.hideShowOptions[option]
    })
    .addCase(setHideShowOption, (state, action) => {
      const { option, isEnabled } = action.payload
      state.hideShowOptions[option] = isEnabled
    })
    .addCase(resetBuildingElementsStatus, (state) => {
      state.buildingElementStatus = initialState.buildingElementStatus
    })
    .addCase(resetConstructionState, () => {
      return initialState
    })
    .addCase(moveRoof, (state, action) => {
      const { id, offset } = action.payload
      const roofs = state.convertData.building.roofs
      const roof = roofs.find((roof) => roof.id === id)

      if (roof) {
        roof.surfaces.forEach((surface) => {
          surface.polygon.nodes.forEach((node) => {
            node.point.x += offset.x
            node.point.y += offset.y
            node.point.z += offset.z
          })

          surface.polygon.edges.forEach((edge) => {
            edge.node1.point.x += offset.x
            edge.node1.point.y += offset.y
            edge.node1.point.z += offset.z

            edge.node2.point.x += offset.x
            edge.node2.point.y += offset.y
            edge.node2.point.z += offset.z
          })

          surface.rafters.forEach((rafter) => {
            rafter.center_point.x += offset.x
            rafter.center_point.y += offset.y
            rafter.center_point.z += offset.z
          })
        })

        roof.hips.forEach((hip) => {
          hip.center_point.x += offset.x
          hip.center_point.y += offset.y
          hip.center_point.z += offset.z
        })

        roof.ridges.forEach((ridge) => {
          ridge.center_point.x += offset.x
          ridge.center_point.y += offset.y
          ridge.center_point.z += offset.z

          if (ridge.posts) {
            ridge.posts.forEach((post) => {
              post.center_point.x += offset.x
              post.center_point.y += offset.y
              post.center_point.z += offset.z
            })
          }
        })
      }
    })
})

export default constructionReducer
